import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../services/axiosInstance";
import {
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Box,
  IconButton,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import PlaceIcon from "@mui/icons-material/Place";
import TitleIcon from "@mui/icons-material/Title";
import BadgeIcon from "@mui/icons-material/Badge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import API_BASE_URL from "../config";

const Signup = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    city: "",
    user_id: "",
    post_job: false,
    post_service: false,
    post_ad: false,
    post_offer: false,
    post_event: false,
    post_classified: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cityEditable, setCityEditable] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const autoDetectCity = async () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await axios.get(
              `https://geocode.maps.co/reverse?lat=${latitude}&lon=${longitude}`
            );
            const detectedCity = response.data.address?.city || "";
            if (detectedCity) {
              setUser((prevUser) => ({ ...prevUser, city: detectedCity }));
              setCityEditable(false);
            } else {
              setCityEditable(true);
            }
          } catch (error) {
            console.error("❌ Error fetching city:", error);
            setCityEditable(true);
          }
        },
        (error) => {
          console.error("❌ Geolocation error:", error);
          setCityEditable(true);
        }
      );
    } else {
      setCityEditable(true);
    }
  };

  useEffect(() => {
    autoDetectCity();
  }, []);

  const validateForm = () => {
    const errors = {};

    if (!user.name.trim()) {
      errors.name = "Full name is required.";
    }

    if (!user.email.includes("@") || !/\S+@\S+\.\S+/.test(user.email)) {
      errors.email = "Please enter a valid email.";
    }

    if (!user.password || user.password.length < 7) {
      errors.password = "Password must be at least 6 characters.";
    }

    if (!user.phone || !/^\d{10,15}$/.test(user.phone)) {
      errors.phone = "Enter a valid phone number (10–15 digits).";
    }

    if (!user.user_id.trim()) {
      errors.user_id = "User ID is required.";
    }

    if (!user.city.trim()) {
      errors.city = "City is required.";
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setLoading(false);
      return;
    }

    setFieldErrors({});

    try {
      const response = await axios.post(`${API_BASE_URL}/users/register`, user);
      alert("Signup Successful! Redirecting to login.");
      navigate("/login");
    } catch (error) {
      console.error("❌ Error signing up:", error.response?.data || error.message);
      setError(error.response?.data?.error || "Signup failed. Please try again.");
    }

    setLoading(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 5, mb: 4 }}>
      <Card sx={{ p: 3, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h4" color="#42326e" fontWeight="bold" textAlign="center">
            Sign Up
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <TitleIcon color="action" />
              <TextField
                fullWidth
                label="Full Name"
                variant="outlined"
                value={user.name}
                onChange={(e) => setUser({ ...user, name: e.target.value })}
                required
                error={!!fieldErrors.name}
                helperText={fieldErrors.name}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <EmailIcon color="action" />
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                type="email"
                value={user.email}
                onChange={(e) => setUser({ ...user, email: e.target.value })}
                required
                error={!!fieldErrors.email}
                helperText={fieldErrors.email}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <LockIcon color="action" />
              <TextField
                fullWidth
                label="Password"
                variant="outlined"
                type={showPassword ? "text" : "password"}
                value={user.password}
                onChange={(e) => setUser({ ...user, password: e.target.value })}
                required
                error={!!fieldErrors.password}
                helperText={fieldErrors.password}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={togglePasswordVisibility} edge="end">
                      {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  ),
                }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <PhoneIcon color="action" />
              <TextField
                fullWidth
                label="Phone"
                variant="outlined"
                value={user.phone}
                onChange={(e) => setUser({ ...user, phone: e.target.value })}
                required
                error={!!fieldErrors.phone}
                helperText={fieldErrors.phone}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <BadgeIcon color="action" />
              <TextField
                fullWidth
                label="User ID"
                variant="outlined"
                value={user.user_id}
                onChange={(e) => setUser({ ...user, user_id: e.target.value })}
                required
                error={!!fieldErrors.user_id}
                helperText={fieldErrors.user_id}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <PlaceIcon color="action" />
              <TextField
                fullWidth
                label="City"
                variant="outlined"
                value={user.city}
                onChange={(e) => setUser({ ...user, city: e.target.value })}
                disabled={!cityEditable}
                error={!!fieldErrors.city}
                helperText={fieldErrors.city}
              />
            </Box>

            {cityEditable && (
              <Typography variant="body2" sx={{ mt: 1, color: "red" }}>
                *City not detected? Enter it manually.
              </Typography>
            )}

            <Button
              variant="contained"
              fullWidth
              sx={{
                mt: 3,
                backgroundColor: "#42326e",
                color: "white",
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
              type="submit"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Sign Up"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Signup;
